import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import {
  CHURN_LEVEL_ORDER,
  type ChurnRiskLevel,
  type UsageSeatBands,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import type {
  UsageInsight,
  UsageInsightKind,
} from "@/v2-backoffice-client/usage/scope/usage.scope.insights"
import {
  SCORE_LEVEL_ORDER,
  type ScoreLevel,
} from "@/v2-backoffice-client/usage/usage.constants"

/**
 * Churn-risk levels the global filter (and the risk donut) offers — the
 * canonical order; "none" never occurs in the portfolio (seats required).
 */
export const CHURN_FILTER_LEVELS: Exclude<ChurnRiskLevel, "none">[] =
  CHURN_LEVEL_ORDER

/** Engagement-score levels the global activity filter offers, best first. */
export const SCORE_FILTER_LEVELS: ScoreLevel[] = SCORE_LEVEL_ORDER

/**
 * Color breaks for the portfolio scatter, most urgent first. One level
 * finer than churn alone: "critical" separates accounts that are BOTH at
 * high risk AND still losing usage — the call-today pile — from high-risk
 * accounts that have at least stopped falling.
 */
export type PortfolioHealthBreak =
  | "critical"
  | "high"
  | "medium"
  | "low"
  | "new"

export const HEALTH_BREAKS: PortfolioHealthBreak[] = [
  "critical",
  "high",
  "medium",
  "low",
  "new",
]

export const HEALTH_BREAK_LABELS: Record<PortfolioHealthBreak, string> = {
  critical: "At risk & falling",
  high: "High risk",
  medium: "Needs attention",
  low: "Healthy",
  new: "No baseline",
}

export const HEALTH_BREAK_COLORS: Record<PortfolioHealthBreak, string> = {
  critical: DATA_VIZ.semantic.negative,
  high: DATA_VIZ.series[6],
  medium: DATA_VIZ.brand.amber,
  low: DATA_VIZ.semantic.positive,
  new: DATA_VIZ.semantic.neutral,
}

/**
 * The four working segments the Health tab splits the book into — each one
 * maps to a different conversation, so the tile label IS the to-do.
 */
export type PortfolioSegmentKey =
  | "activation-fires"
  | "fit-risk"
  | "idle-expansion"
  | "champions"

export const PORTFOLIO_SEGMENT_ORDER: PortfolioSegmentKey[] = [
  "activation-fires",
  "fit-risk",
  "idle-expansion",
  "champions",
]

export const PORTFOLIO_SEGMENT_LABELS: Record<PortfolioSegmentKey, string> = {
  "activation-fires": "At risk & disengaged",
  "fit-risk": "At risk despite engagement",
  "idle-expansion": "Healthy core, idle seats",
  champions: "Healthy & activated",
}

export const PORTFOLIO_SEGMENT_ACTIONS: Record<PortfolioSegmentKey, string> = {
  "activation-fires": "Run onboarding/activation before the renewal call.",
  "fit-risk":
    "Engaged users, shrinking footprint — a product-fit talk, not training.",
  "idle-expansion":
    "The engaged core carries idle licenses — onboard the quiet seats.",
  champions: "Reference and expansion candidates — build on the win.",
}

export const PORTFOLIO_SEGMENT_COLORS: Record<PortfolioSegmentKey, string> = {
  "activation-fires": DATA_VIZ.semantic.negative,
  "fit-risk": DATA_VIZ.series[6],
  "idle-expansion": DATA_VIZ.brand.amber,
  champions: DATA_VIZ.semantic.positive,
}

/**
 * Sort/filter order of play tones on the Portfolio playbook: what to DO
 * (action) comes before where to GROW (opportunity), which comes before
 * what to CELEBRATE (positive).
 */
export const PLAY_TONE_ORDER: Record<UsageInsight["tone"], number> = {
  action: 0,
  opportunity: 1,
  positive: 2,
}

/**
 * Account-card orderings. "Urgency" is the derived default (tone → churn →
 * deadline → money → seats); the rest are the axes a CS person actually
 * plans by on a given day.
 */
export type PlaybookSort = "urgency" | "renewal" | "value" | "seats" | "name"

export const PLAYBOOK_SORTS: { key: PlaybookSort; label: string }[] = [
  { key: "urgency", label: "By urgency" },
  { key: "renewal", label: "By renewal date" },
  { key: "value", label: "By value" },
  { key: "seats", label: "By seats" },
  { key: "name", label: "By name" },
]

/** How long the copy button shows its tick. */
export const PLAYBOOK_COPY_FEEDBACK_MS = 1500

/** Chip labels per play tone. */
export const PLAY_TONE_LABELS: Record<UsageInsight["tone"], string> = {
  action: "Actions",
  opportunity: "Openings",
  positive: "Wins",
}

/**
 * The playbook's chapters. Actions split by urgency: an account at high
 * churn risk or with a renewal or trial end inside RENEWAL_SOON_DAYS is
 * this week's call; the rest of the actions are the watch list.
 */
export type PlaybookChapterKey =
  | "act-now"
  | "watch"
  | "opportunity"
  | "positive"

export const PLAYBOOK_CHAPTER_ORDER: PlaybookChapterKey[] = [
  "act-now",
  "watch",
  "opportunity",
  "positive",
]

export const PLAYBOOK_CHAPTER_LABELS: Record<PlaybookChapterKey, string> = {
  "act-now": "Act now",
  watch: "Keep an eye on",
  opportunity: "Openings",
  positive: "Wins",
}

/** One plain sentence under each chapter heading saying what lands there. */
export const PLAYBOOK_CHAPTER_LEADS: Record<PlaybookChapterKey, string> = {
  "act-now":
    "Something to fix on an account at high churn risk, or with a renewal or trial end inside 90 days: seats gone quiet, usage dropping, licenses nobody uses.",
  watch:
    "The same signals on accounts with time: no renewal in sight and the churn read below high. Worth a look, not a call this week.",
  opportunity:
    "Accounts with an expansion angle: use cases nobody has opened, a Console v2 demo, an API-license conversation.",
  positive:
    "Accounts where the story is good: growing usage to anchor a renewal on, or a reference customer to ask.",
}

/**
 * Bold skim-label per play kind — the move at a glance, so a card of six
 * plays can be triaged without reading six sentences.
 */
export const PLAY_KIND_LABELS: Record<UsageInsightKind, string> = {
  "idle-seats": "Activate idle seats",
  "newly-quiet": "Gone quiet",
  "falling-users": "Usage dropping",
  "untouched-areas": "Untouched use cases",
  automation: "API-license opening",
  champions: "Anchor the renewal",
  steady: "Reference customer",
  "v1-only": "Console v2 demo",
  "v1-fading": "v1 fading — check in",
  migration: "Migration under way",
  "v2-pilot": "Support the v2 pilots",
}

/**
 * How far past a renewal/trial date it still surfaces as "overdue" instead
 * of vanishing as stale CRM data. A lapsed renewal is exactly the date a CS
 * person needs to chase — hiding it made the renewals list read empty.
 */
export const RENEWAL_OVERDUE_GRACE_DAYS = 45

/**
 * Churn-risk rank for play prioritization (higher = more urgent). "new" and
 * "none" rank with "low": a scope without a baseline isn't a fire.
 */
export const PLAY_CHURN_RANK: Record<ChurnRiskLevel, number> = {
  high: 2,
  medium: 1,
  low: 0,
  new: 0,
  none: 0,
}

/**
 * A contract-renewal / trial-expiry deadline within this window renders as
 * a countdown on plays and movers — close enough that the play should be
 * run before the money conversation happens.
 */
export const RENEWAL_SOON_DAYS = 90

/** Within this window the countdown renders as a warning, not a note. */
export const RENEWAL_URGENT_DAYS = 30

/**
 * Minimum raw 28-day events (current or previous window) for a customer to
 * qualify for the movers lists — a 3-event scope trending +300% is noise,
 * not a mover.
 */
export const MOVER_ACTIVITY_FLOOR_EVENTS = 30

/** Customers shown per movers list (rising / falling). */
export const MOVER_LIMIT = 5

/** Customer groups shown in the playbook before "Show all". */
export const PLAYBOOK_PREVIEW_CUSTOMERS = 25

/**
 * Chart fills per churn level (Recharts needs concrete values — CSS vars
 * don't reach SVG fills). Hue language matches the usage pills: coral
 * act-now, amber attention, green healthy, slate no-signal.
 */
export const CHURN_LEVEL_CHART_COLORS: Record<
  Exclude<ChurnRiskLevel, "none">,
  string
> = {
  high: DATA_VIZ.semantic.negative,
  medium: DATA_VIZ.brand.amber,
  low: DATA_VIZ.semantic.positive,
  new: DATA_VIZ.semantic.neutral,
}

/**
 * Chart fills per seat-engagement band — same story the stacked license bar
 * tells (usage-seat-segment-* classes), in chart-safe concrete colors.
 */
export const SEAT_BAND_CHART_COLORS: Record<keyof UsageSeatBands, string> = {
  power: DATA_VIZ.brand.greenBright,
  healthy: DATA_VIZ.brand.green3,
  cooling: DATA_VIZ.brand.amber,
  dormant: DATA_VIZ.semantic.negative,
  quiet: DATA_VIZ.semantic.neutral,
}

/**
 * Chart fills per product-mix state: v1 amber and v2 green match the app
 * colors everywhere else; "both" gets the sanctioned categorical teal so it
 * reads as its own stage, not a blend.
 */
export const MIX_STATE_CHART_COLORS: Record<PortfolioMixState, string> = {
  "v1-only": DATA_VIZ.brand.amber,
  dual: DATA_VIZ.series[2],
  "v2-only": DATA_VIZ.brand.green2,
  inactive: DATA_VIZ.semantic.neutral,
}

/**
 * Geography-map layer colors, shared by the map symbols and the section's
 * toggle/legend dots. They live here (not in PortfolioGeoMap) so the section
 * never statically imports the map module — that static edge is what pulled
 * @arcgis/core into the initial bundle and broke its module-evaluation order
 * (the map is loaded lazily instead).
 */
export const GEO_WORK_COLOR = DATA_VIZ.brand.green2
export const GEO_SIGNIN_COLOR = DATA_VIZ.series[2]

/**
 * Customer console-mix states over the last 28 days, for the mix funnel.
 * The funnel is the v1 → v2 migration journey, so it reads the consoles
 * only: a customer working through the MCP servers alone counts as
 * "inactive" here — no console activity — and is the MCP tab's story.
 */
export type PortfolioMixState = "v1-only" | "dual" | "v2-only" | "inactive"

export const MIX_STATE_LABELS: Record<PortfolioMixState, string> = {
  "v1-only": "Console v1 only",
  dual: "Both consoles",
  "v2-only": "Console v2 only",
  inactive: "No console activity",
}

/** Rendering order of the mix funnel: the migration journey left to right. */
export const MIX_STATE_ORDER: PortfolioMixState[] = [
  "v1-only",
  "dual",
  "v2-only",
  "inactive",
]
